'use client';

import { SiNextdotjs, SiCloudflare, SiDocker, SiNodedotjs, SiPython, SiGo, SiDotnet, SiRust, SiPhp, SiVercel } from 'react-icons/si';
import { FaJava } from 'react-icons/fa';

export default function SupportedPlatformsSection() {
  const methods = [
    {
      name: 'Node.js SDK',
      tag: 'Recommended',
      icon: SiNodedotjs,
      color: '#339933',
      desc: 'One line in any Node app - Express, Next.js, Nest. Protects inbound + outbound traffic, including data leaks a WAF can\u2019t see.',
      code: 'npm i @silker-ai/agent',
    },
    {
      name: 'Docker Container',
      tag: 'Self-host',
      icon: SiDocker,
      color: '#2496ED',
      desc: 'Run the proxy in front of any app, on any host. PHP, Java, Python, Go \u2014 no code changes required.',
      code: 'docker run ghcr.io/niceappspl/silker-proxy',
    },
    {
      name: 'Cloudflare Worker',
      tag: 'Beta',
      icon: SiCloudflare,
      color: '#F38020',
      desc: 'Deploy a Worker on your Cloudflare zone. Any backend, zero code changes \u2014 we run inside Cloudflare.',
      code: 'wrangler deploy',
    },
  ];

  const protectedStacks = [
    { name: 'Node.js', icon: SiNodedotjs, color: '#339933' },
    { name: 'Next.js', icon: SiNextdotjs, color: '#FFFFFF' },
    { name: 'Python', icon: SiPython, color: '#3776AB' },
    { name: 'Go', icon: SiGo, color: '#00ADD8' },
    { name: 'Java', icon: FaJava, color: '#f89820' },
    { name: '.NET', icon: SiDotnet, color: '#512BD4' },
    { name: 'PHP', icon: SiPhp, color: '#777BB4' },
    { name: 'Rust', icon: SiRust, color: '#FFFFFF' },
  ];

  return (
    <section className="sm:px-6 lg:px-8 sm:py-16 max-w-6xl mx-auto py-12 px-4">
      <div className="text-center mb-12">
        <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-3">
          One engine. Three ways to deploy.
        </h2>
        <p className="text-neutral-400 text-sm sm:text-base max-w-2xl mx-auto">
          The same detection engine, wherever your app lives. Pick the path with the least friction for your stack.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-16">
        {methods.map((m, index) => {
          const Icon = m.icon;
          return (
            <div
              key={index}
              className="flex flex-col p-6 rounded-none border border-white/5 bg-neutral-900/30 hover:bg-neutral-900/60 hover:border-white/10 transition-all duration-300 group"
            >
              <div className="flex items-center justify-between mb-4">
                <Icon className="w-9 h-9 transition-transform group-hover:scale-110 duration-300" style={{ color: m.color }} />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-rose-400 px-2 py-1 rounded-none bg-rose-500/10 border border-rose-500/20">
                  {m.tag}
                </span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{m.name}</h3>
              <p className="text-sm text-neutral-400 leading-relaxed mb-4 flex-1">{m.desc}</p>
              <code className="text-xs font-mono text-neutral-300 bg-black/40 px-3 py-2 rounded-none border border-white/5">
                {m.code}
              </code>
            </div>
          );
        })}
      </div>

      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-3">
          Protects any stack
        </h2>
        <p className="text-neutral-400 text-sm sm:text-base">
          Silker works at the HTTP layer - it doesn&apos;t care what your backend is written in.
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-4 mb-12">
        {protectedStacks.map((item, index) => {
          const IconComponent = item.icon;
          return (
            <div
              key={index}
              className="flex flex-col items-center justify-center gap-3 px-6 py-5 rounded-none border border-white/5 bg-neutral-900/10 hover:bg-neutral-900/30 hover:border-white/10 transition-all duration-300 w-36 group"
            >
              <IconComponent
                className="w-8 h-8 transition-transform group-hover:scale-110 duration-300"
                style={{ color: item.color }}
              />
              <span className="text-xs text-neutral-400 group-hover:text-white transition-colors font-medium text-center">
                {item.name}
              </span>
            </div>
          );
        })}
      </div>

      <div className="mt-12 text-center pt-8 border-t border-white/5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-neutral-400">
        <span className="inline-flex items-center gap-2"><SiVercel className="w-3.5 h-3.5" style={{ color: '#fff' }} /> Vercel, Netlify, Railway, any host</span>
        <span className="text-neutral-700">•</span>
        <span>Your traffic never passes through our servers</span>
        <span className="text-neutral-700">•</span>
        <span>No vendor lock-in</span>
      </div>
    </section>
  );
}
